import { useEffect, useRef } from 'react';
import { useMatches, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

interface RedirectObject {
  [slugMatch: string]: string | undefined;
}

export const useRedirectObject = (redirectObject: RedirectObject): void => {
  const isRedirected = useRef(false);
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (Object.keys(redirectObject).length === 0 || !Object.values(redirectObject).every(Boolean)) {
      return;
    }

    const params = matches[matches.length - 1]?.params || {};
    const pathPattern =
      '/' +
      location.pathname
        .split('/')
        .filter(Boolean)
        .map((segment) => {
          const paramKey = Object.keys(params).find((key) => params[key] === segment);

          return paramKey ? `:${paramKey}` : segment;
        })
        .join('/');

    const redirectPath = Object.entries(redirectObject).reduce(
      (acc, [key, value]) => acc.replace(key, value!),
      pathPattern,
    );

    const redirectUrl = searchParams.toString() ? `${redirectPath}?${searchParams.toString()}` : redirectPath;
    const redirectUrlWithHash = location.hash ? `${redirectUrl}${location.hash}` : redirectUrl;
    const currentUrl = location.pathname + location.search + location.hash;

    if (currentUrl !== redirectUrlWithHash && !isRedirected.current) {
      isRedirected.current = true;
      navigate(redirectUrlWithHash, { replace: true });
    }
  }, [redirectObject, matches, location, navigate, searchParams]);
};
